/**
 * Статусы «Работает ли» раздела EO «Виджет» (01.11, ТЗ 1.17 EMB-019 ч. 3, §0.6 п. 70): понятные человеку «норма / внимание /
 * сбой / нет данных» с причинами словами — цвет на экране только дублирует подпись. Пороги — те же, что у тревог V4 (OPS-011,
 * INTERNAL 0.5 §7, apps/api/src/utils/v2Alerts.ts WATCH): «молчит» сервер считает сам (silent — правилом тревоги, только при
 * активном ключе), здесь — только чтение полей.
 * Чистые функции, тест — widgetStatus.test.ts.
 */
import type { ApiHost, HealthSite, InstanceHealth } from "./widgetTypes";

export type Tone = "ok" | "warn" | "fail" | "none";
export const TONE_LABEL: Readonly<Record<Tone, string>> = { ok: "норма", warn: "внимание", fail: "сбой", none: "нет данных" };
export interface Verdict { tone: Tone; label: string; reasons: string[] }

/** Пороги (те же, что OPS-011 и INTERNAL §7): перезапусков за час, дней сертификата, возраст generated_at, застывший таймер. */
export const HEALTH = { restarts: 3, tlsDays: 14, genAgeS: 15 * 60, synthStaleMin: 30, stealthStaleH: 26, originsAgeS: 900, synthFails: 2, silentMin: 15 } as const;

const ageMin = (iso: string | null | undefined, now: number): number | null => {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? Math.floor((now - ms) / 60_000) : null;
};
const verdict = (fail: string[], warn: string[], none = false): Verdict => {
  const tone: Tone = fail.length ? "fail" : warn.length ? "warn" : none ? "none" : "ok";
  return { tone, label: TONE_LABEL[tone], reasons: [...fail, ...warn] };
};

/** Экземпляр прокладки: сводки, v4_auth, самопроверка, скрытность, список Origin, перезапуски, контракт. */
export function instanceVerdict(i: InstanceHealth, activeKey: boolean, now: number): Verdict {
  if (i.last_at === null) {
    return activeKey ? verdict(["сводок от экземпляра ещё не было, а сайт v2 включён"], []) : verdict([], [], true);
  }
  const fail: string[] = [], warn: string[] = [];
  if (i.silent) fail.push(`молчит: сводки нет ${i.silent_min ?? "?"} мин (порог — 15 мин)`);
  /* без активного ключа сервер «молчит» не ставит — как тревога proxy_silent (ревью b235d4e4); давность сводки — видна */
  else if (!activeKey && (i.silent_min ?? 0) > HEALTH.silentMin) warn.push(`сводки нет ${i.silent_min} мин — сайтов v2 с действующим ключом нет, тревоги нет`);
  if (i.v4_auth === "off") fail.push("V4 не пустил экземпляр (v4_auth: off) — секрет или адрес не совпали");
  const s = i.synth;
  if (s?.ok === false && (s.fails_in_row ?? 0) >= HEALTH.synthFails) fail.push(`самопроверка провалилась ${s.fails_in_row} раз подряд`);
  else if (s?.ok === false) warn.push("самопроверка провалилась один раз");
  if (i.stealth?.ok === false) fail.push("проверка скрытности не прошла — снаружи могут быть видны следы Cloudflare");
  if (i.origins?.source === "none") fail.push("нет списка доменов сайтов — все сайты получают отказ");
  if ((i.restarts_1h ?? 0) >= HEALTH.restarts) warn.push(`перезапусков за час: ${i.restarts_1h}`);
  if (s?.tls_days != null && s.tls_days < HEALTH.tlsDays) warn.push(`сертификат истекает через ${s.tls_days} дн.`);
  if (s?.gen_age_s != null && s.gen_age_s > HEALTH.genAgeS) warn.push("расписание контрольного квеста старше 15 мин");
  const sa = ageMin(s?.at, now);
  if (sa !== null && sa > HEALTH.synthStaleMin) warn.push(`самопроверка не обновлялась ${sa} мин`);
  const st = ageMin(i.stealth?.at, now);
  if (st !== null && st > HEALTH.stealthStaleH * 60) warn.push(`проверка скрытности не обновлялась ${Math.floor(st / 60)} ч`);
  const o = i.origins;
  if (o?.source === "file") warn.push("список доменов — только копия с диска: V4 не отвечает экземпляру");
  if (o && o.age_s != null && o.age_s > HEALTH.originsAgeS) warn.push("список доменов не обновлялся дольше 15 мин");
  if (o && ((o.dropped ?? 0) > 0 || (o.rejected ?? 0) > 0)) warn.push("экземпляр отбросил негодные домены из списка V4");
  if (i.contract_ok === false) warn.push("версия контракта у экземпляра другая, чем у V4");
  if (i.capped_at) warn.push("у экземпляра больше 4 адресов — наблюдение неполно");
  return verdict(fail, warn);
}

/** Адрес кода установки: ровно один экземпляр называет его в сводке (INTERNAL §9.1). */
export function hostVerdict(h: ApiHost): Verdict {
  if (h.instances.length > 1) return verdict([`один адрес у ${h.instances.length} экземпляров: ${h.instances.join(", ")}`], []);
  if (h.instances.length === 0) return verdict([], ["нет экземпляра: ни одна сводка не называет этот адрес"]);
  return verdict([], []);
}

/** Сайт v2: ключ, конфиг, выключатель, текст согласия, запросы с доменов. Выключенный v2 — «нет данных» с подписью «v2 выключен». */
export function siteVerdict(s: HealthSite): Verdict {
  if (!s.enabled) return { tone: "none", label: "v2 выключен", reasons: [] };
  const fail: string[] = [], warn: string[] = [];
  if (!s.active) fail.push("ключ отозван — сайт получает отказ");
  if (!s.config_ok) fail.push("конфиг не проходит схему — сайт закрыт");
  if (s.disabled) warn.push("включён выключатель: все кнопки ведут на запасной путь");
  if (!s.pd) warn.push("нет текста согласия на обработку ПДн");
  if (!s.seen.length) warn.push("запросов с доменов сайта в сводках нет");
  return verdict(fail, warn);
}

/** Тревога по коду: «сбой» — молчание, закрытый шлюз, 5xx, синтетика, повтор хеша; прочее — «внимание». */
export function alertTone(code: string): Tone {
  const base = code.split(":")[0] ?? code;
  return ["proxy_silent", "proxies_none", "proxy_5xx", "ops011_v4", "synth_fail", "hash_dup"].includes(base) ? "fail" : "warn";
}
